import { ProgramStatsResponse, UserId } from '@cibo/core'
import { useProgramStats } from '@cibo/programs'
import { intuitiveFromNow, useFigures } from '@cibo/ui'
import { Box, Grid, Skeleton, Stack, Typography } from '@mui/material'
import { UseQueryResult } from '@tanstack/react-query'
import numeral from 'numeral'
import { useTranslation } from 'react-i18next'

const StatsRow = ({ label, value }: { label: string; value?: string }) => (
  <>
    <Grid item xs={6}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Grid>
    <Grid item xs={6}>
      <Typography color="text.secondary" style={{ fontWeight: 700 }}>
        {value}
      </Typography>
    </Grid>
  </>
)

const StatsSkeleton = ({ label }: { label: string }) => (
  <>
    <Grid item xs={6}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Grid>
    <Grid item xs={6}>
      <Typography color="text.secondary" style={{ fontWeight: 700 }}>
        <Skeleton />
      </Typography>
    </Grid>
  </>
)

export const ProgramsUserStats = () => <ProgramsStats statsQuery={useProgramStats({})} />
export const ProgramsOrgStats = (props: { orgId?: string; userId?: UserId }) => (
  <ProgramsStats statsQuery={useProgramStats(props)} />
)

const ProgramsStats = ({ statsQuery }: { statsQuery: UseQueryResult<ProgramStatsResponse> }) => {
  const { t } = useTranslation('impact/ProgramsStats')
  const { acres, earnedAmount } = useFigures()

  const earnings = statsQuery.data?.earnings
  const firstEnrollmentDate = statsQuery.data?.firstEnrollmentDate

  return (
    <Stack spacing={2}>
      {!!earnings && (
        <Stack spacing={1}>
          <Typography variant="h5" color="text.secondary">
            {t('totalEarnings')}
          </Typography>
          <Stack>
            {earnings.map(({ amount, currency }) => (
              <Typography variant="body2">
                {currency ? earnedAmount({ amount, token: currency }) : <Skeleton width={75} />}
              </Typography>
            ))}
          </Stack>
        </Stack>
      )}
      <Stack spacing={1}>
        <Typography variant="h5" color="text.secondary">
          {t('stats')}
        </Typography>
        <Box>
          <Grid spacing={1} container>
            {!!statsQuery.isFetched ? (
              <>
                <StatsRow
                  label={t('firstEnrollment')}
                  value={firstEnrollmentDate ? intuitiveFromNow(firstEnrollmentDate) : '--'}
                />
                <StatsRow
                  label={t('activePrograms')}
                  value={numeral(statsQuery.data?.enrolledPrograms).format('0,0')}
                />
                <StatsRow
                  label={t('enrolledFields')}
                  value={numeral(statsQuery.data?.enrolledFields).format('0,0')}
                />
                <StatsRow
                  label={t('enrolledAcres')}
                  value={acres(statsQuery.data?.enrolledAcres || 0)}
                />
              </>
            ) : (
              <>
                <StatsSkeleton label={t('firstEnrollment')} />
                <StatsSkeleton label={t('activePrograms')} />
                <StatsSkeleton label={t('enrolledFields')} />
                <StatsSkeleton label={t('enrolledAcres')} />
              </>
            )}
          </Grid>
        </Box>
      </Stack>
    </Stack>
  )
}
